import { makeStyles } from '@material-ui/styles';
import { Tabs } from 'antd';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { bookingJourneyType } from 'common/constants/booking';
import BookingTableDetails from './booking-table-details';
import BookingStageAndPropertyNameFilters from './booking-stage-and-property-name-filters';

const useStyles = makeStyles(theme => ({
  wrapper: {
    minHeight: 300,

    '& .ant-tabs-bar': {
      margin: '0',
    },

    '& .ant-tabs-tab': {
      padding: '0 24px !important',
      marginRight: '4px !important',

      '&.ant-tabs-tab-active': {
        position: 'relative',
        color: `${theme.colors.titleColor} !important`,
        ...theme.typography.h5,
      },

      '&:hover': {
        color: `${theme.colors.titleColor} !important`,
      },
    },
  },
  tables: {
    padding: '16px 24px',
    borderRadius: `0 0 ${theme.borderRadius.small}px ${theme.borderRadius.small}px`,
    backgroundColor: theme.colors.gray1,
    border: `1px solid ${theme.colors.borderColor}`,
    borderTop: 'none',
  },
}));

const BookingTables = ({
  hasManual,
  hasMessageProperty,
  className,
  bookingType,
  setBookingType,
  bookingStage,
  setBookingStage,
  otherTableFilter,
  setOtherTableFilter,
  tableData,
  tableLoading,
  refetchTableData,
}) => {
  const classes = useStyles();
  const { t } = useTranslation(['landlord', 'cms']);

  const isHerculesBooking = useMemo(
    () => bookingType === bookingJourneyType.SEMI_AUTOMATIC,
    [bookingType],
  );

  const handleClickTab = type => {
    setBookingType(type);
  };

  const isManual = bookingType === bookingJourneyType.MANUAL;
  const data = isManual
    ? tableData?.landlord?.unionBookings ?? { edges: [] }
    : tableData?.listOrders ?? { edges: [] };

  return (
    <div className={classNames(classes.wrapper, className)}>
      <Tabs onChange={handleClickTab} type="card" activeKey={bookingType}>
        <If condition={hasMessageProperty}>
          <Tabs.TabPane
            tab={t('landlord:landlord.long_tail.booking.list.hercules_booking_title')}
            key={bookingJourneyType.SEMI_AUTOMATIC}
          />
        </If>

        <If condition={hasManual}>
          <Tabs.TabPane
            tab={t('landlord:landlord.long_tail.booking.list.manual_booking_title')}
            key={bookingJourneyType.MANUAL}
          />
        </If>
      </Tabs>
      <div className={classes.tables}>
        <If condition={!isManual}>
          <BookingStageAndPropertyNameFilters
            setBookingStage={setBookingStage}
            bookingStage={bookingStage}
            bookingType={bookingType}
          />
        </If>
        <BookingTableDetails
          tableData={data}
          loading={tableLoading}
          isHerculesBooking={isHerculesBooking}
          otherTableFilter={otherTableFilter}
          setOtherTableFilter={setOtherTableFilter}
          refetchData={refetchTableData}
          bookingType={bookingType}
        />
      </div>
    </div>
  );
};

BookingTables.propTypes = {
  className: PropTypes.string,
  bookingType: PropTypes.string,
  setBookingType: PropTypes.func,
  bookingStage: PropTypes.string,
  setBookingStage: PropTypes.func,
  otherTableFilter: PropTypes.object,
  setOtherTableFilter: PropTypes.func,
  tableData: PropTypes.object,
  tableLoading: PropTypes.bool,
  refetchTableData: PropTypes.func,
};

export default BookingTables;
